import { FACET_ALL_VALUE, type FacetSelectOption } from '@/components/system';
import { CATEGORY_ORDER } from '@/lib/instrument-categories';
import { parseInstrumentName } from '@/lib/instrument-name';
import type { Test } from '@/api/tests';

interface InstrumentFilters {
    query: string;
    category: string;
}

/**
 * Category facet options with their counts, ordered the way the catalog reads
 * rather than alphabetically: `CATEGORY_ORDER` groups related conditions, and
 * anything the ordering does not know about is appended in pt-BR collation.
 */
export function buildCategoryOptions(tests: Test[]): FacetSelectOption[] {
    const counts = new Map<string, number>();
    for (const test of tests) {
        if (!test.category) continue;
        counts.set(test.category, (counts.get(test.category) ?? 0) + 1);
    }

    const orderedCategories = [
        ...CATEGORY_ORDER.filter((name) => counts.has(name)),
        ...[...counts.keys()]
            .filter((name) => !CATEGORY_ORDER.includes(name))
            .sort((a, b) => a.localeCompare(b, 'pt-BR')),
    ];

    return orderedCategories.map((name) => ({
        value: name,
        label: name,
        count: counts.get(name),
    }));
}

/**
 * The catalog's search and sort, shared by the `/assets` page and the patient
 * assignment picker so the same query surfaces the same instruments in both.
 *
 * The query matches the description even though neither surface renders it —
 * psychologists search by what an instrument is for, and the description is
 * where that wording lives.
 */
export function filterAndSortInstruments(tests: Test[], { query, category }: InstrumentFilters) {
    const normalizedQuery = query.trim().toLowerCase();

    const categoryRank = (test: Test) => {
        if (!test.category) return CATEGORY_ORDER.length;
        const index = CATEGORY_ORDER.indexOf(test.category);
        return index === -1 ? CATEGORY_ORDER.length : index;
    };

    return tests
        .filter((test) => {
            if (category !== FACET_ALL_VALUE && test.category !== category) return false;
            if (!normalizedQuery) return true;

            return (
                test.name?.toLowerCase().includes(normalizedQuery) ||
                test.description?.toLowerCase().includes(normalizedQuery) ||
                test.test_id?.toLowerCase().includes(normalizedQuery) ||
                test.category?.toLowerCase().includes(normalizedQuery)
            );
        })
        .sort((a, b) => categoryRank(a) - categoryRank(b) || a.name.localeCompare(b.name, 'pt-BR'));
}

/**
 * Acronyms that more than one instrument in `tests` resolves to.
 *
 * Only for these is the name's trailing variant worth rendering: SCARED and
 * SCAS each exist as a child and a parents version sharing both the acronym and
 * the name, so without the suffix the two rows are indistinguishable. For a
 * unique acronym the same suffix is just noise ("16-30 meses" on the sole
 * M-CHAT-R restates the audience the row deliberately drops).
 */
export function findAmbiguousAcronyms(tests: Test[]): Set<string> {
    const counts = new Map<string, number>();
    for (const test of tests) {
        const { acronym } = parseInstrumentName(test.name, test.test_id);
        counts.set(acronym, (counts.get(acronym) ?? 0) + 1);
    }

    return new Set(
        [...counts.entries()].filter(([, count]) => count > 1).map(([acronym]) => acronym),
    );
}
